import { computed, ComputedRef } from "vue";
import { useUserNotificationsStore } from "@/features/user-notifications/store/user-notifications-store";
import { UserNotification } from "@/features/user-notifications/types/user-notifications-interface";
import { NotificationType } from "../types/notification-types";
import { Routes, router } from "@/router";
import {
  getNotificationIcon,
  getNotificationText,
} from "../types/notification-type-functions";

export function useNotificationItem(notification: UserNotification) {
  const notificationIcon = computed(() => {
    return getNotificationIcon(notification.type as NotificationType);
  });

  const notificationText = computed(() => {
    return getNotificationText(notification.type as NotificationType);
  });

  return {
    notificationIcon,
    notificationText,
  };
}

export function useNotifications() {
  const store = useUserNotificationsStore();

  // Computed properties
  const isLoading: ComputedRef<boolean> = computed(() => store.getIsLoading);
  const chartGroupId: ComputedRef<number | null> = computed(
    () => store.getChartGroupId
  );
  const currentPage: ComputedRef<number> = computed(
    () => store.pagination.currentPage
  );
  const totalPages: ComputedRef<number> = computed(
    () => store.pagination.totalPages
  );
  const notifications: ComputedRef<UserNotification[]> = computed(
    () => store.notifications
  );
  const hasUnreadNotifications: ComputedRef<boolean> = computed(
    () =>
      store.notifications.filter((n: UserNotification) => !n.read).length > 0
  );

  const handleNotificationClick = (notification: UserNotification) => {
    if (chartGroupId.value !== null) {
      store.markNotificationAsRead(chartGroupId.value, notification.id);
    }
    switch (notification.type) {
      case "PatientMessage":
      case "PatientConversation":
        router.goTo(Routes.messages);
        break;
      case "Letter":
        router.push({
          name: Routes.patientDocuments,
          query: { documentType: "letters" },
        });
        break;
      case "FilledPdfForm":
        router.push({
          name: Routes.patientDocuments,
          query: { documentType: "forms" },
        });
        break;
      case "messageFile":
        router.push({
          name: Routes.patientDocuments,
          query: { documentType: "messageFiles" },
        });
        break;
      case "ImportedFile":
        router.push({
          name: Routes.patientDocuments,
          query: { documentType: "otherDocuments" },
        });
        break;
      case "Result":
        router.goTo(Routes.testResults);
        break;
      case "Appointment":
        router.goTo(Routes.appointments);
        break;
      case "Careplan":
        router.goTo(Routes.carePlans);
        break;
    }
  };

  // Handler for infinite scrolling
  const handleScroll = (e: Event) => {
    const target = e.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    if (scrollTop + clientHeight + 10 >= scrollHeight) {
      if (currentPage.value < totalPages.value) {
        if (chartGroupId.value) {
          store.fetchAdditionalNotifications(chartGroupId.value);
        }
      }
    }
  };

  // Functions to bypass notification system and conditionally check if it is read
  const isNotificationUnread = (
    notificationableId: number,
    notificationableType: string
  ): boolean => {
    return store.notifications.some(
      (notification) =>
        notification.notificationableId === notificationableId &&
        notification.type === notificationableType &&
        !notification.read
    );
  };

  const markNotificationAsRead = (notificationableId: number) => {
    const notification = store.notifications.find(
      (n) => n.notificationableId === notificationableId && !n.read
    );

    if (notification && chartGroupId.value) {
      store.markNotificationAsRead(chartGroupId.value, notification.id);
    }
  };

  const markMessageNotificationsAsRead = () => {
    store.notifications.forEach((notification) => {
      if (
        notification.type === "PatientMessage" ||
        notification.type === "PatientConversation"
      ) {
        if (chartGroupId.value) {
          store.markNotificationAsRead(chartGroupId.value, notification.id);
        }
      }
    });
  };

  return {
    store,
    notifications,
    isLoading,
    currentPage,
    totalPages,
    hasUnreadNotifications,
    nothingToShowMessage: "You have no notifications to show",
    handleNotificationClick,
    handleScroll,
    isNotificationUnread,
    markNotificationAsRead,
    markMessageNotificationsAsRead,
  };
}
